import React, { FC } from "react"
import { useSelector } from "react-redux"
import { getPageSets } from "../reducers/pageSets"
import { RoundedRectangle } from "../components/views/atoms/roundedrectangle/RoundedRectangle"
import { RRNavLink } from "../components/views/atoms/roundedrectangle/RRNavLink"
import { BodyWithLeftSidebarLayout } from "../components/views/atoms/Layouts"
import { PageHeader } from "../components/views/molecules/Molecules"
import { MasterLayout } from "../components/views/atoms/layouts/MasterLayout"
import { Redirect, Route, Switch, useLocation } from "react-router-dom"
import { Page } from "../components/dynamic-content/Page"
import { Paths } from "../utils/Paths"
import styled from "styled-components"
import { IPage } from "contentful-types"
import { ContentfulConditionalGate } from "../lib/contentful/components/ContentfulConditionGate"

const YourBenefitGuide: FC = () => {
  const pageSets = useSelector(getPageSets)

  if (!pageSets) {
    return null
  }
  // maintaining the naming convention in here to match Contentful PageSet's name 'digital_benefits_guide'
  const digitalBenefitsGuideFields = pageSets?.items.find((pageSet) => pageSet.fields.key === "digital_benefits_guide")
    .fields
  const topLevelPages = digitalBenefitsGuideFields.topLevelPages

  return (
    <MasterLayout
      header={
        <PageHeader icon={digitalBenefitsGuideFields.icon.fields.file.url} title={digitalBenefitsGuideFields.title} />
      }
    >
      <BodyWithLeftSidebarLayout sideBarElements={[<DigitalBenefitsGuideNav pages={topLevelPages} key="DBG_nav" />]}>
        <div className="w-100">
          <Switch>
            <Redirect
              exact
              from={Paths.YOUR_BENEFIT_GUIDE}
              to={`${Paths.YOUR_BENEFIT_GUIDE}/${topLevelPages[0].fields.slug}`}
            />
            <RoutesToPages pages={topLevelPages} baseUrl={Paths.YOUR_BENEFIT_GUIDE} />
          </Switch>
        </div>
      </BodyWithLeftSidebarLayout>
    </MasterLayout>
  )
}

type RoutesToPagesFields = {
  pages: IPage[]
  baseUrl: string
}

const RoutesToPages: FC<RoutesToPagesFields> = ({ pages, baseUrl }) => {
  return (
    <>
      {pages?.map(
        (page) =>
          page.fields && (
            <div key={page.sys.id}>
              <Route exact path={`${baseUrl}/${page.fields.slug}`} render={() => <Page page={page} />} />
              <RoutesToPages pages={page.fields.childPages} baseUrl={`${baseUrl}/${page.fields.slug}`} />
            </div>
          )
      )}
    </>
  )
}
type DigitalBenefitsGuideNavFields = {
  pages: IPage[]
}

const DigitalBenefitsGuideNav: FC<DigitalBenefitsGuideNavFields> = ({ pages }) => {
  return (
    <nav className="flex flex-column flex-row-l">
      <RoundedRectangle backgroundColor="mint-alternate" className={"w-220px-l"}>
        {pages?.map(
          (page) =>
            page.fields && (
              <ContentfulConditionalGate conditions={page.fields.conditions} key={page.sys.id}>
                <PageNav page={page} baseSlug={`${Paths.YOUR_BENEFIT_GUIDE}`} top={pages[0] === page} />
              </ContentfulConditionalGate>
            )
        )}
      </RoundedRectangle>
    </nav>
  )
}

const ChildPagesAnimation = styled.div`
  max-height: 0;
  transition: 0.25s ease-out;
  overflow-y: hidden;
  &.show-child-links {
    max-height: 500px;
    transition: max-height ease-in 0.25s;
  }
`

// We had to set the overflow-x in a child div so that the little white line to the left would show when it's active.
// Setting overflow-x and overflow-y on the same div didn't work :'(
// https://stackoverflow.com/questions/6421966/css-overflow-x-visible-and-overflow-y-hidden-causing-scrollbar-issue
const ChildPagesWrapper = styled.div`
  margin-left: 16px;
  overflow-x: visible;
`
type PageNavFields = {
  page: IPage
  baseSlug: string
  top?: boolean | false
}
const PageNav: FC<PageNavFields> = ({ page, baseSlug, top = false }) => {
  const { pathname } = useLocation()

  const {
    fields: { navigationText, slug, childPages }
  } = page

  const linkTo = `${baseSlug}/${slug}`
  const active = linkTo === pathname

  const getShowChildLinksClass = () =>
    active || childPages?.some((page) => `${linkTo}/${page.fields.slug}` === pathname) ? "show-child-links" : ""

  return (
    <>
      <RRNavLink to={linkTo} width="w-33" active={active} top={top}>
        {navigationText}
      </RRNavLink>
      <ChildPagesAnimation className={getShowChildLinksClass()}>
        <ChildPagesWrapper>
          {childPages?.map((page) => (
            <ChildPageNav page={page} baseSlug={linkTo} key={page.sys.id} />
          ))}
        </ChildPagesWrapper>
      </ChildPagesAnimation>
    </>
  )
}
type ChildPageNavFields = {
  page: IPage
  baseSlug: string
}
const ChildPageNav: FC<ChildPageNavFields> = ({ page, baseSlug }) =>
  page.fields && (
    <ContentfulConditionalGate conditions={page.fields.conditions}>
      <PageNav page={page} baseSlug={baseSlug} />
    </ContentfulConditionalGate>
  )

export { YourBenefitGuide }
